import {
  Box,
  Checkbox,
  Text,
} from '@chakra-ui/react';
import React, { useRef, useCallback } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';

import { IconCross } from '../../../../../styles/icons';
import { SelectInput, TextAreaInput } from '../../../../components/forms';

import { useUtilityDataSelector } from '../../../../utilities/contexts';
import {updateProfileValue} from "../../../../redux/actions/user";
import Lozenge from "../../../../components/common/Lozenge";
import dayjs from "dayjs";
import {Button, Input} from "@headlessui/react";
import {FormItemTitle} from "../FormItemTitle";
import {MdDelete} from "react-icons/md";

const AboutMeForm = (props) => {
  const { profile } = props;
  const allLanguages = useUtilityDataSelector((utilityData) => utilityData.languages);

  const dispatch = useDispatch();
  const formAboutMe = useRef();

  //set local state
  const [hiddenGender, setHiddenGender] = useState(profile.hiddenSearchValues?.includes('gender') ?? false);
  const [languageSelection, setLanguageSelection] = useState(allLanguages);
  const [practiceStartDate, setPracticeStartDate] = useState(
    profile.practiceStartDate
  );
  // Get the client groups utility data
  const allTherapistTitles = useUtilityDataSelector(
    (utilityData) => utilityData.therapistTitles
  );
  const allGenders = useUtilityDataSelector(utilData => utilData.genders);

  // Set state
  const [therapistTitleSelection, setTherapistTitleSelection] = useState(allTherapistTitles);
  const [therapistGender, setTherapistGender] = useState(allGenders);

  //integrate form with redux
  const [hasLoaded, setHasLoaded] = useState(true);


  const handleProfileValueUpdate = useCallback((key, value) => {
    dispatch(updateProfileValue(key, value))
  }, [dispatch]);

  useEffect(() => {
    if(!allLanguages || !allTherapistTitles || !allGenders) {
      return;
    }

    // initialise languages
    const tLangs = allLanguages.map((item) => {
      return profile.languages.map((elem) => elem.value).includes(item.value)
        ? { ...item, selected: true }
        : { ...item, selected: false };
    });
    setLanguageSelection(tLangs);

    const tTitles = allTherapistTitles.map((item) => {
      return profile.therapistTitles
        .map((elem) => elem.value)
        .includes(item.value)
        ? { ...item, selected: true }
        : { ...item, selected: false };
    });
    setTherapistTitleSelection(tTitles);

    const tGenders = allGenders.map((item) => {
      return profile.gender
        .map((elem) => elem.value)
        .includes(item.value)
        ? { ...item, selected: true }
        : { ...item, selected: false };
    });
    setTherapistGender(tGenders);

    setHasLoaded(true);
  }, [allGenders, allLanguages, allTherapistTitles, profile]);

  // update languages
  const updateLanguageSelection = useCallback((val, action) => {
    let newArr =
      action === 'add'
        ? languageSelection.map((elem) => ({
            ...elem,
            selected: Number(elem.value) === Number(val) ? true : elem.selected,
          }))
        : languageSelection.map((elem) => ({
            ...elem,
            selected:
              Number(elem.value) === Number(val) ? false : elem.selected,
          }));
    setLanguageSelection(newArr);
    handleProfileValueUpdate('languages', newArr.filter(el => el.selected));
  }, [handleProfileValueUpdate, languageSelection]);

  const renderLanguagePills = useCallback(() => {
    const mandatoryPill = (
      <Box
        id='English'
        backgroundColor="gray.400"
        height="32px"
        borderRadius="16px"
        display="flex"
        justifyContent="center"
        alignItems="center"
        padding="5px 20px 5px 20px "
        marginRight="5px"
        marginBottom="10px"
      >
        <Text textStyle="h6" pr="5px" color="white">
          English
        </Text>
      </Box>
    );

    const otherPills = languageSelection
      .filter((elem) => elem.selected && elem.label !== 'English')
      .map((elem, idx) => (
        <Box
          id={idx}
          key={idx}
          backgroundColor={elem.label === 'English' ? 'gray.400' : 'brand.100'}
          height="32px"
          borderRadius="16px"
          display="flex"
          justifyContent="center"
          alignItems="center"
          padding="5px 20px 5px 20px "
          marginRight="5px"
          marginBottom="10px"
        >
          <Text textStyle="h6" pr="5px">
            {elem.label}
          </Text>
          <IconCross
            size="16px"
            onClick={() => updateLanguageSelection(elem.value, 'remove')}
          />
        </Box>
      ));
    return (
      <>
        {mandatoryPill}
        {otherPills}
      </>
    );
  }, [languageSelection, updateLanguageSelection]);

  // Wait for state to load
  if (!hasLoaded) return null;

  // Render
  return (
    <Formik
      innerRef={formAboutMe}
      initialValues={{
        aboutMe: profile.aboutMe === null ? '' : profile.aboutMe,
        summary: profile.summary === null ? '' : profile.summary,
        //practiceStartDate: profile.practiceStartDate,
        //languages: profile.languages,
      }}
      validationSchema={Yup.object({
        aboutMe: Yup.string(),
      })}
      onSubmit={() => {}}
    >
      {(formik) => (
        <div className="flex flex-col rounded items-start gap-6 w-full bg-white p-4">
          <div className="w-full">
            <FormItemTitle title="How would you describe your job title?" required />
            <p className='pb-2 text-sm'>You may select up to 3 titles</p>

            <Lozenge
              title=""
              data={therapistTitleSelection}
              onclick={(e) => {
                setTherapistTitleSelection(e);
                handleProfileValueUpdate('therapistTitles', e.filter(el => el.selected));
              }}
              multi
              max={3}
            />
          </div>
          <div className="w-full">
            <FormItemTitle
              title="Provide a brief introduction"
              label='This is the first bit of text that will be displayed to clients in the search results'
              required
            />

            <TextAreaInput
              bg="brand.100"
              width="100%"
              height="100px"
              name="summary"
              placeholder={`Provide a short intro description (180 characters maximum)`}
              maxLength={180}
              id="summary"
              onChange={e => {
                formik.handleChange(e);
                handleProfileValueUpdate('summary', e.target.value);
              }}
            />
            <Box width="100%" display="flex" justifyContent="flex-end">
              <Text textStyle="body2" color="brand.200">
                {document.getElementById('summary')?.value.length}/180
              </Text>
            </Box>
          </div>
          <div className="w-full">
            <FormItemTitle title='About me' required />
            <TextAreaInput
              bg="brand.100"
              width="100%"
              height="300px"
              name="aboutMe"
              onChange={e => {
                formik.handleChange(e);
                handleProfileValueUpdate('aboutMe', e.target.value)
              }}
              placeholder="Tell potential clients how you work and what issues you have experience with. This will help potential clients understand your skills and aproach in more detail."
            />
          </div>
          <div className="w-full">
            <FormItemTitle
              title='When did you start your practice?'
              label='This will convert to ‘years of practice’ on your profile. If you decide you don’t want to show this, you can use the bin icon to clear your answer'
            />
            <div className='w-full flex gap-2'>
              <Input
                className='bg-brand-off rounded w-full max-w-[300px] border-0'
                type='date'
                max={dayjs().format('YYYY-MM-DD')}
                value={dayjs(practiceStartDate).format('YYYY-MM-DD')}
                onChange={(e) => {
                  setPracticeStartDate(new Date(e.target.value).toISOString());
                  handleProfileValueUpdate('practiceStartDate', new Date(e.target.value));
                }}
              />
              {practiceStartDate && (
                <Button
                  onClick={() => {
                    setPracticeStartDate(null);
                    handleProfileValueUpdate('practiceStartDate', null);
                  }}
                  className='rounded p-2 group bg-white hover:bg-red-400 transition'>
                  <MdDelete className='w-5 h-5 text-red-500 group-hover:text-white'/>
                </Button>
              )}
            </div>
          </div>
          <div className='w-full flex flex-col gap-2'>
            <div className="w-full">
              <FormItemTitle title='Gender' required />
              <Lozenge
                title=""
                data={therapistGender}
                onclick={(e) => {
                  setTherapistGender(e);
                  handleProfileValueUpdate('gender', e.filter(el => el.selected));
                }}
              />
            </div>
            <div className='flex items-center'>
              <Checkbox
                colorScheme='green'
                isChecked={hiddenGender}
                onChange={() => {
                  const newArray = hiddenGender ? profile.hiddenSearchValues.filter(item => item !== 'gender') : [...profile.hiddenSearchValues, 'gender'];
                  setHiddenGender(!hiddenGender);

                  handleProfileValueUpdate('hiddenSearchValues', newArray);
                }}>
                Hide from public searches
              </Checkbox>
              <FormItemTitle
                label="This means you are happy for Welldoing to have this information, but you don't want to be searchable by this filter on the public directory. The Welldoing team may use this information to help them service MatchPlus clients who make specific requests."
              />
            </div>
          </div>
          <div className="w-full">
            <FormItemTitle title='Do you speak other languages in addition to English?' required />
            <Box display="flex" alignItems="center" marginTop="16px">
              {renderLanguagePills()}
            </Box>
            <SelectInput
              name="languages"
              variant="filled"
              placeholder="Select a language to add it to the list"
              onChange={(e) => {
                updateLanguageSelection(e.target.value, 'add');
              }}
            >
              {allLanguages?.map((item, idx) => {
                return (
                  <option key={idx} value={item.value}>
                    {item.label}
                  </option>
                );
              })}
            </SelectInput>
          </div>
        </div>
      )}
    </Formik>
  );
};

export default AboutMeForm;
